import { memo } from "react";
import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";
import { Amount } from "app/components/ui/Amount/Amount";
import PriceTag, { PRICE_TAG_VARIANTS } from "app/components/ui/PriceTag/PriceTag";
import QuotationLuggageSelect from "app/pages/Booking/Quotation/Luggage/QuotationLuggageSelect/QuotationLuggageSelect";
import { messagePropType } from "app/utils/propTypes";
import "./QuotationLuggageItem.scss";
import classNames from "classnames";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";
import { BAGGAGE_TYPES } from "app/constants";
import SavingsHighlight from "app/components/ui/SavingsHighlight/SavingsHighlight";

/**
 * @param baggageCount
 * @param picto
 * @param label
 * @param totalUpgradePrice
 * @param informativeOnly no interaction, not input, for information purposes only
 * @param publicUpgradePrice
 * @param isIncluded
 * @param canBeBooked
 * @param description
 * @param details
 * @param flightBagageOptions
 * @param onLuggageChange
 * @param displayFlightPriceInfoBubble
 * @param luggageType
 */
const QuotationLuggageItem = ({
	baggageCount = 0,
	picto,
	label,
	totalUpgradePrice,
	informativeOnly,
	publicUpgradePrice,
	isIncluded,
	canBeBooked,
	description,
	details,
	flightBagageOptions = [],
	onLuggageChange,
	displayFlightPriceInfoBubble = false,
	luggageType,
}) => {
	const showBaggageSelect = !informativeOnly && isIncluded === false && canBeBooked;

	const quotationLuggageItemTopClassName = classNames("quotation-luggage-item__top", {
		"quotation-luggage-item__top-not-included": baggageCount === 0,
	});

	const discountedAmount = (publicUpgradePrice || 0) - (totalUpgradePrice || 0);

	const renderPrice = () => {
		const isOffered = totalUpgradePrice === 0;
		const shouldDefaultToZero =
			typeof totalUpgradePrice === "undefined" && !isIncluded && canBeBooked;

		if (informativeOnly && shouldDefaultToZero) {
			return (
				<div className="quotation-luggage-item__add-later-label">
					<FormattedMessage id="quotation.luggage.add.later.label" />
				</div>
			);
		}

		if (isIncluded) {
			return <PriceTag />;
		}

		if (isOffered) {
			return <PriceTag variant={PRICE_TAG_VARIANTS.OFFERED} />;
		}

		return (
			<Amount
				sign="+"
				signIsBold={false}
				value={shouldDefaultToZero ? 0 : totalUpgradePrice}
				size="large"
			/>
		);
	};

	return (
		<article className="quotation-luggage-item">
			<header className={quotationLuggageItemTopClassName}>
				<span aria-hidden="true" className="quotation-luggage-item__picto">
					{picto}
				</span>
				<div className="quotation-luggage-item__info" data-testid="luggage-detail">
					<div className="quotation-luggage-item__count-label">
						{baggageCount >= 0 && (
							<Typography
								variant={TYPOGRAPHY_VARIANTS.REGULAR}
								variantMD={TYPOGRAPHY_VARIANTS.LARGE}
								isBold
								component={"span"}
								className="quotation-luggage-item__count"
							>
								{baggageCount} x
							</Typography>
						)}
						<Typography
							variant={TYPOGRAPHY_VARIANTS.REGULAR}
							variantMD={TYPOGRAPHY_VARIANTS.LARGE}
							component={"span"}
							className="quotation-luggage-item__label"
						>
							{label}
						</Typography>
					</div>

					{details && (
						<Typography
							variant={TYPOGRAPHY_VARIANTS.SMALL}
							variantMD={TYPOGRAPHY_VARIANTS.REGULAR}
							className="quotation-luggage-item__details"
						>
							{details}
						</Typography>
					)}
				</div>

				<aside className="quotation-luggage-item__prices" data-testid="luggage-price">
					<span className="quotation-luggage-item__upgrade-price">{renderPrice()}</span>

					{discountedAmount > 0 && (
						<SavingsHighlight
							messageKey="general.save.amount"
							amountSaved={discountedAmount}
							trackEvent="luggage_click_savings"
							trackData={{
								baggageCount: baggageCount,
							}}
							description={
								displayFlightPriceInfoBubble && (
									<FormattedMessage id="quotation.flight.luggage.with.discount" />
								)
							}
						/>
					)}
				</aside>
			</header>

			{!informativeOnly && description && (
				<Typography
					variant={TYPOGRAPHY_VARIANTS.REGULAR}
					className="quotation-luggage-item__message"
					data-testid="luggage-description"
					component="p"
				>
					{description}
				</Typography>
			)}

			{showBaggageSelect && (
				<div className="quotation-luggage-item__input">
					<QuotationLuggageSelect
						flightBaggageOptions={flightBagageOptions}
						onBaggageChange={onLuggageChange}
						luggageType={luggageType}
					/>
				</div>
			)}
		</article>
	);
};

QuotationLuggageItem.propTypes = {
	baggageCount: PropTypes.number,
	onLuggageChange: PropTypes.func,
	totalUpgradePrice: PropTypes.number,
	publicUpgradePrice: PropTypes.number,
	flightBagageOptions: PropTypes.array,
	picto: PropTypes.element,
	label: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
	details: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
	isIncluded: PropTypes.bool,
	canBeBooked: PropTypes.bool,
	informativeOnly: PropTypes.bool,
	displayFlightPriceInfoBubble: PropTypes.bool,
	description: messagePropType,
	luggageType: PropTypes.oneOf(Object.values(BAGGAGE_TYPES)),
};

export default memo(QuotationLuggageItem);
